import {
  compose,
  flattenProp,
  withHandlers,
  withStateHandlers,
  lifecycle,
} from 'recompose';
import { connect } from 'react-redux';
import {
  functionalGroup,
  visibleTemplateIds,
  submittedTemplateIds,
  submittedQuantityByFunctionalGroup,
} from 'reducers/functionalGroups/selectors';
import { duplicateTemplateId, templates } from 'reducers/templates/selectors';
import { theme, mini } from 'reducers/dashboard/selectors';
import { selectedOptions } from 'reducers/search/selectors';
import combineSelectors from 'utils/combineSelectors';
import { addProps, addIntlProps } from 'utils/recompose';
import { injectIntl } from 'react-intl';
import messages from './messages';
import formatCurrency from 'utils/formatCurrency';
import { intersection, map, isEqual } from 'lodash';
import {
  functionalOptionCode,
  functionalOptionCodeMappings,
} from 'reducers/functionalOptionCodeMappings/selectors';
import { activeFilters } from 'reducers/templateFilters/selectors';
import { clearAllDesignOptionFilters } from 'reducers/templateFilters/actions';
import { maximumVisibleTemplates } from 'constants/values';

const mapStateToProps = combineSelectors({
  functionalOptionCode,
  functionalOptionCodeMappings,
  functionalGroup,
  visibleTemplateIds,
  submittedTemplateIds,
  submittedQuantityByFunctionalGroup,
  duplicateTemplateId,
  selectedOptions,
  theme,
  mini,
  activeFilters,
  fullTemplates: templates,
});

export const buttonTheming = ({ theme }) => ({
  color: theme.button.activeText,
  border: theme.button.activeBorder,
});

const formatUpcharge = formatCurrency({
  priceProp: 'upcharge',
  currencyProp: 'currency',
});

export const initialState = {
  showDescription: false,
  isClicked: false,
  isOpen: false,
};

export const toggleDescription = ({ isOpen, onClick, isClicked }) => () => {
  onClick && onClick();
  if (!isClicked) return { isClicked: true, isOpen: true };
  return { isClicked: !isClicked, isOpen: !isOpen };
};

const handleMouseOver = ({ isClicked, onMouseEnter }) => () => {
  onMouseEnter && onMouseEnter();
  if (!isClicked) return { isOpen: true };
};

const handleMouseLeave = ({ isClicked, onMouseLeave }) => () => {
  onMouseLeave && onMouseLeave();
  if (!isClicked) return { isOpen: false };
};

export const functionalGroupQuantity = ({
  inReviewView,
  suggestedQuant,
  submittedQuantityByFunctionalGroup,
}) => (inReviewView ? suggestedQuant : submittedQuantityByFunctionalGroup);

export const hasSubmittedTemplates = ({ submittedTemplateIds }) =>
  submittedTemplateIds.length > 0;

export const hasInReviewTemplates = ({ visibleTemplateIds }) =>
  visibleTemplateIds.length > 0;

export const hasDuplicate = ({ functionalGroup, duplicateTemplateId }) =>
  functionalGroup.templates.includes(duplicateTemplateId);

export const matchingSearchTemplates = ({
  selectedOptions,
  functionalGroup,
}) => {
  if (selectedOptions && selectedOptions.length > 0) {
    let templateIds = map(selectedOptions, o => o.templateIds);
    let templatesForOptions = intersection(...templateIds);
    let searchTemplates = intersection(
      functionalGroup.templates,
      templatesForOptions,
    );
    return [...searchTemplates];
  } else {
    return [];
  }
};

export const onFindMore = ({ triggerSearch, functionalGroupCodes }) => () => {
  triggerSearch({ functionalGroupCodes });
};

const formatDataTemplate = (dataTemplate, maximumVisibleTemplates) => {
  var templateData = [];
  let lengthTemplate =
    dataTemplate.length <= maximumVisibleTemplates
      ? dataTemplate.length
      : maximumVisibleTemplates;
  if (dataTemplate.length >= 1) {
    for (var i = 0; i < lengthTemplate; i++) {
      templateData.push(dataTemplate[i]);
    }
  }
  return templateData;
};

const extractOptionsFromTemplate = (templateIds, modelTemplates, options) => {
  if (!templateIds.length) return;
  const currentTemplateId = templateIds[0];
  const { designPackage } = modelTemplates[currentTemplateId];
  for (let key in designPackage) {
    const designOptions = options[key];
    const designOptionId = designPackage[key].id;
    if (key !== 'imgUrls' && !designOptions.includes(designOptionId))
      designOptions.push(designOptionId);
  }
  extractOptionsFromTemplate(
    templateIds.slice(1, templateIds.length),
    modelTemplates,
    options,
  );
};

export const getDesignOptions = (
  functionalGroupTemplateIds,
  modelTemplates,
) => {
  const options = {
    interiorOption: [],
    paintOption: [],
    trimOption: [],
    wheelOption: [],
    colorLineOption: [],
    roofAndMirrorCapOption: [],
  };
  extractOptionsFromTemplate(
    functionalGroupTemplateIds,
    modelTemplates,
    options,
  );

  return options;
};

export const showFilters = (
  { areFiltersVisible },
  { templates, fullTemplates },
) => () => {
  const designOptions = getDesignOptions(templates, fullTemplates);
  return {
    areFiltersVisible: !areFiltersVisible,
    functionalGroupTemplates: !areFiltersVisible
      ? formatDataTemplate(templates, maximumVisibleTemplates)
      : [],
    initDataTemplate: templates,
    maximumVisibleTemplates: maximumVisibleTemplates,
    designOptions,
  };
};

export const hideFilters = () => () => {
  return {
    areFiltersVisible: false,
    functionalGroupTemplates: [],
  };
};

const checkOptionMatch = (
  designPackageOption = {},
  activeFilterOptions = [],
) => {
  if (!activeFilterOptions.length) {
    return false;
  }
  if (designPackageOption.id === activeFilterOptions[0].id) {
    return true;
  }
  return checkOptionMatch(
    designPackageOption,
    activeFilterOptions.slice(1, activeFilterOptions.length),
  );
};

const getMatchingTemplate = (template = {}, activeFilters = {}) => {
  const { designPackage } = template;
  const designOptions = Object.keys(activeFilters);
  let doAllKeysMatch = false;
  for (let option of designOptions) {
    doAllKeysMatch = checkOptionMatch(
      designPackage[option],
      activeFilters[option],
    );
    if (!doAllKeysMatch) break;
  }
  return doAllKeysMatch;
};

const isInViewport = props => () => {
  let element = document.getElementById('template');
  var templateData = [...props.functionalGroupTemplates];
  let counterTemplateSum = props.maximumVisibleTemplates;
  if (element.scrollTop >= element.scrollHeight - element.offsetHeight - 60) {
    counterTemplateSum = props.maximumVisibleTemplates + 20;
    if (counterTemplateSum > props.initDataTemplate.length) {
      counterTemplateSum = props.initDataTemplate.length;
    }
    for (var i = props.maximumVisibleTemplates; i < counterTemplateSum; i++) {
      templateData.push(props.initDataTemplate[i]);
    }
  }
  return {
    maximumVisibleTemplates: counterTemplateSum,
    functionalGroupTemplates:
      templateData.length >= 1 ? templateData : props.functionalGroupTemplates,
  };
};
const shoudlFiltersHide = (prevProps, props) =>
  prevProps.inReviewView && !props.inReviewView;

const getVisibleTemplates = (
  templates,
  functionalGroupId,
  fullTemplates,
  activeFilters,
) => {
  return templates.filter(templateId =>
    getMatchingTemplate(
      fullTemplates[templateId],
      activeFilters[functionalGroupId],
    ),
  );
};

function componentDidUpdate(prevProps) {
  const {
    activeFilters,
    functionalGroupId,
    setFunctionalGroupTemplates,
    fullTemplates,
    templates,
  } = this.props;

  if (
    !isEqual(
      prevProps.activeFilters[functionalGroupId],
      activeFilters[functionalGroupId],
    ) &&
    activeFilters[functionalGroupId]
  ) {
    const visibleTemplates = getVisibleTemplates(
      templates,
      functionalGroupId,
      fullTemplates,
      activeFilters,
    );
    setFunctionalGroupTemplates(visibleTemplates);
  }
  if (shoudlFiltersHide(prevProps, this.props)) {
    this.props.hideFilters();
    this.props.clearAllDesignOptionFilters();
  }
  if (
    !prevProps.searchView &&
    this.props.searchView &&
    !Object.keys(activeFilters).length
  ) {
    this.props.hideFilters();
  }
}

const setFunctionalGroupTemplates = ({ areFiltersVisible }) => templates => {
  return {
    areFiltersVisible,
    initDataTemplate: templates,
    functionalGroupTemplates: formatDataTemplate(
      templates,
      maximumVisibleTemplates,
    ),
  };
};

const showWarning = () => () => {
  return { isWarningVisible: true };
};

const hideWarning = () => () => {
  return { isWarningVisible: false };
};

const clearGlobalFilters = ({
  closeGolbalSearch,
  hideWarning,
  showFilters,
}) => () => {
  closeGolbalSearch();
  hideWarning();
  showFilters();
};

const onSearchWithinGroup = ({
  selectedOptions,
  showWarning,
  showFilters,
  closeGolbalSearch,
  searchView,
}) => () => {
  if (selectedOptions && selectedOptions.length) {
    showWarning();
    return;
  }
  if (searchView) closeGolbalSearch();
  showFilters();
};

const onRemoveFilter = ({
  activeFilters,
  functionalGroupId,
  setFunctionalGroupTemplates,
  fullTemplates,
  templates,
}) => () => {
  const filtersInFunctionalGroup = activeFilters[functionalGroupId];
  const visibleTemplates = getVisibleTemplates(
    templates,
    functionalGroupId,
    fullTemplates,
    activeFilters,
  );
  if (!Object.keys(filtersInFunctionalGroup).length) {
    setFunctionalGroupTemplates(templates);
    return;
  }
  setFunctionalGroupTemplates(visibleTemplates);
};

function componentWillUnmount() {
  this.props.clearAllDesignOptionFilters();
}

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: [
      'unitsString',
      'quantityString',
      'designPackageString',
      'designPriceString',
      'priceBrandString',
      'totalPriceString',
      'suggestedString',
      'priorityHeader',
      'currency',
      'noSubmittedTemplatesNotice',
      'noInReviewTemplatesNotice',
      'findMore',
      'noSearchResultsNotice',
    ],
  }),
  connect(mapStateToProps, {
    clearAllDesignOptionFilters,
  }),
  flattenProp('functionalGroup'),
  addProps({
    formatUpcharge,
    functionalGroupQuantity,
    hasSubmittedTemplates,
    hasDuplicate,
    hasInReviewTemplates,
    matchingSearchTemplates,
    buttonTheming,
  }),
  withStateHandlers(initialState, {
    toggleDescription,
    handleMouseOver,
    handleMouseLeave,
  }),
  withStateHandlers(
    {
      areFiltersVisible: false,
      functionalGroupTemplates: [],
      isWarningVisible: false,
      actualDataTemplate: [],
      designOptions: {
        interiorOption: [],
        paintOption: [],
        trimOption: [],
        wheelOption: [],
        colorLineOption: [],
        roofAndMirrorCapOption: [],
      },
    },
    {
      showFilters,
      setFunctionalGroupTemplates,
      hideFilters,
      showWarning,
      hideWarning,
      isInViewport,
    },
  ),
  withHandlers({ onFindMore, onSearchWithinGroup, clearGlobalFilters }),
  lifecycle({ componentDidUpdate, componentWillUnmount }),
  withHandlers({ onRemoveFilter }),
);
